import { Controller } from "@hotwired/stimulus"
import gsap from "gsap"
import ScrollTrigger from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export default class extends Controller {
  connect() {
    this.initAnimations()
  }

  initAnimations() {
    this.animateHero()
    this.animateTechStack()
    this.animateMethodologySection()
    this.animateExpertiseCards()
  }

  animateHero() {
    const hero = this.element.querySelector('.hero-section')
    if (!hero) return

    const elements = hero.querySelectorAll('h1, p')

    gsap.from(elements, {
      y: 40,
      opacity: 0,
      duration: 0.8,
      stagger: 0.15,
      ease: "power3.out"
    })
  }

  animateTechStack() {
    const techIcons = this.element.querySelectorAll('section[aria-labelledby="tech-stack-heading"] > div')

    gsap.from(techIcons, {
      scrollTrigger: {
        trigger: techIcons[0],
        start: "top 80%",
        toggleActions: "play none none reverse"
      },
      scale: 0,
      opacity: 0,
      duration: 0.5,
      stagger: 0.08,
      ease: "back.out(1.7)"
    })
  }

  animateMethodologySection() {
    const methodologySection = this.element.querySelector('.relative.isolate.overflow-hidden')
    if (!methodologySection) return

    const heading = methodologySection.querySelector('h2')
    const paragraphs = methodologySection.querySelectorAll('p')
    const listItems = methodologySection.querySelectorAll('li')
    const image = methodologySection.querySelector('img')

    if (heading) {
      gsap.from(heading, {
        scrollTrigger: {
          trigger: heading,
          start: "top 80%",
          toggleActions: "play none none reverse"
        },
        y: 30,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out"
      })
    }

    paragraphs.forEach((p, index) => {
      gsap.from(p, {
        scrollTrigger: {
          trigger: p,
          start: "top 85%",
          toggleActions: "play none none reverse"
        },
        y: 20,
        opacity: 0,
        duration: 0.7,
        delay: index * 0.1,
        ease: "power2.out"
      })
    })

    if (listItems.length > 0) {
      gsap.from(listItems, {
        scrollTrigger: {
          trigger: listItems[0],
          start: "top 85%",
          toggleActions: "play none none reverse"
        },
        x: -30,
        opacity: 0,
        duration: 0.6,
        stagger: 0.15,
        ease: "power2.out"
      })
    }

    if (image) {
      gsap.from(image, {
        scrollTrigger: {
          trigger: image,
          start: "top 85%",
          toggleActions: "play none none reverse"
        },
        scale: 0.9,
        opacity: 0,
        duration: 1,
        ease: "power3.out"
      })
    }
  }

  animateExpertiseCards() {
    const cards = this.element.querySelectorAll('section[aria-labelledby="development-services-heading"] article')

    cards.forEach((card, index) => {
      const image = card.querySelector('img')
      const content = card.querySelector('.p-10')

      // Main card animation
      gsap.from(card, {
        scrollTrigger: {
          trigger: card,
          start: "top 80%",
          toggleActions: "play none none reverse"
        },
        y: 60,
        opacity: 0,
        duration: 0.9,
        delay: (index % 3) * 0.12,
        ease: "power3.out"
      })

      // Image scale animation
      if (image) {
        const imageWrapper = image.parentElement
        gsap.set(imageWrapper, { overflow: 'hidden' })

        gsap.from(image, {
          scrollTrigger: {
            trigger: card,
            start: "top 80%",
            toggleActions: "play none none reverse"
          },
          scale: 1.15,
          duration: 1.2,
          ease: "power2.out"
        })
      }

      // Content stagger animation
      if (content) {
        const contentElements = content.querySelectorAll('h3, p')

        gsap.from(contentElements, {
          scrollTrigger: {
            trigger: content,
            start: "top 85%",
            toggleActions: "play none none reverse"
          },
          y: 20,
          opacity: 0,
          duration: 0.6,
          stagger: 0.12,
          ease: "power2.out"
        })
      }

      // Hover effects
      this.addCardHoverEffects(card, image)
    })
  }

  addCardHoverEffects(card, image) {
    let hoverTimeline = null

    card.addEventListener('mouseenter', () => {
      if (hoverTimeline) hoverTimeline.kill()

      hoverTimeline = gsap.timeline()
      hoverTimeline.to(card, {
        y: -8,
        duration: 0.4,
        ease: "power2.out"
      })

      if (image) {
        hoverTimeline.to(image, {
          scale: 1.08,
          duration: 0.6,
          ease: "power2.out"
        }, 0)
      }
    })

    card.addEventListener('mouseleave', () => {
      if (hoverTimeline) hoverTimeline.kill()

      hoverTimeline = gsap.timeline()
      hoverTimeline.to(card, {
        y: 0,
        duration: 0.4,
        ease: "power2.out"
      })

      if (image) {
        hoverTimeline.to(image, {
          scale: 1,
          duration: 0.6,
          ease: "power2.out"
        }, 0)
      }
    })
  }

  disconnect() {
    ScrollTrigger.getAll().forEach(trigger => trigger.kill())
  }
}
